import React from "react";
import { getFontAwesomeIcon, formatModalDate } from "../../../utils";
import { GoogleMapsLink } from "../..";

interface Field {
	id?: string;
	labelIcon?: string;
	label?: string;
	valueKey: string;
	inputType?: string;
	labelTextClasses?: string;
	valueTextClasses?: string;
	hasValueLeftIcon?: string;
	hasValueRightIcon?: string;
	hasValueImage?: string;
	detailGridClasses?: string;
	inputGridClasses?: string;
	additionalInputContainerClasses?: string;
	isBoolean?: boolean;
	truthyValue?: string;
	falsyValue?: string;
	isConcatenated?: string;
	isEditOnly?: boolean;
	inputHasLeftIcon?: string;
	inputIconClasses?: string;
	disabledClasses?: string;
	detailIconClasses?: string;
	secondValueTextClasses?: string;
	secondValueKey?: string;
	secondValueLabel?: string;
	hideLabel?: boolean;
	isLink?: string;
	isGoogleMapsLink?: string;
	hoverClasses?: string;
	isDoubleValue?: boolean;
	isListValue?: boolean;
	textAfterValue?: string;
	isDate?: boolean;
}

type Address = {
	addressee?: string;
	street?: string;
	line1?: string;
	line2?: string;
	city?: string;
	zip?: string;
};

interface BaseDetailRowProps {
	field?: Field;
	allFields?: Field[];
	dataItem?: Address | string | number | boolean | undefined;
	allData?: object;
	keyTextColor?: string;
	keyTextSize?: string;
	valueTextColor?: string;
	valueTextSize?: string;
	containerWidth?: string;
	hasValuePrice?: boolean;
	hasValueLongDescription?: boolean;
	iconStyle?: "solid" | "regular" | "light";
	mobileBreakpoint?: string;
	isDisabled?: boolean;
	textSize?: string;
	imageClasses?: string;
	labelClasses?: string;
}

const OldBaseDetailRow: React.FC<BaseDetailRowProps> = ({
	dataItem,
	field,
	allFields,
	allData,
	containerWidth = "w-full",
	iconStyle = "solid",
	mobileBreakpoint,
	isDisabled,
	textSize = "",
	imageClasses = "rounded-full w-18px h-18px",
	labelClasses = "",
}) => {
	const baseDetailClasses = "w-full flex text-left items-center";
	const baseLabelClasses = "pr-2 text-left text-nowrap";

	const renderValue = <T extends React.ReactNode>(
		field: Field,
		value: Address | string | boolean | number | T
	) => {
		if (Array.isArray(value) && field.isDoubleValue) {
 			return (

				<div
					className={`flex flex-col ${
						isDisabled && field.disabledClasses
					}`}
				>
					<>
						<p
							className={`${textSize} ${
								isDisabled && field.disabledClasses
							}`}
						>
							{value[0]}
						</p>
						<p
							className={`${textSize} ${
								field.secondValueTextClasses
							} ${isDisabled && field.disabledClasses}`}
						>
							{value[1]}
						</p>
					</>
				</div>
			);
		} else if (field.isBoolean) {
			return (
				<p
					className={`${textSize}  ${
						isDisabled && field.disabledClasses
					}`}
				>
					{value === true ? field.truthyValue : field.falsyValue}
				</p>
			);
		} else if (field.isDate) {
			return (
				<p
					className={`${textSize}  ${
						isDisabled && field.disabledClasses
					}`}
				>
					{value !== "—" ? formatModalDate(value as string) : value}
				</p>
			);
		} else if (Array.isArray(value) && field.isListValue) {
			return (
				<div>
					{value.length === 0 ? (
						<span>—</span>
					) : (
						<ul
							className={`flex flex-wrap  ${
								isDisabled && field.disabledClasses
							}`}
						>
							{value.map((val, index) => (
								<li
									key={index}
									className={`${textSize}  ${
										isDisabled && field.disabledClasses
									}`}
								>
									<p className="text-nowrap">
										{val?.name ? val?.name : val}
										{index < value.length - 1 && (
											<span className="pr-2">, </span>
										)}
									</p>
								</li>
							))}
						</ul>
					)}
				</div>
			);
		} else if (field.isBoolean) {
			return (
				<p
					className={`${textSize}  ${
						isDisabled && field.disabledClasses
					}`}
				>
					{value === true ? field.truthyValue : field.falsyValue}
				</p>
			);
		} else if (field.isLink) {
			return value !== "—" ? (
				<a
					className={`${textSize} ${
						isDisabled && field.disabledClasses
					}`}
					href={field.isLink && field.isLink}
				>
					{value as string}
				</a>
			) : (
				<span
					className={`${textSize} ${
						isDisabled && field.disabledClasses
					}`}
				>
					—
				</span>
			);
		} else if (field.isGoogleMapsLink) {
			return (
				<GoogleMapsLink
					address={dataItem as Address}
					hoverClasses={field.hoverClasses as string}
				/>
			);
		} else if (field.isConcatenated && dataItem && allData) {
			const secondValue = allFields?.find((singleField) => {
				return singleField.valueKey === field.isConcatenated;
			});

			let secondValueKey = secondValue?.valueKey as keyof typeof dataItem;
			return (
				<p
					className={`${textSize} ${
						isDisabled && field.disabledClasses
					}`}
				>
					{value + " " + allData[secondValueKey]}
				</p>
			);
		} else if (field.secondValueKey) {
			let valueTwo: any =
				field.secondValueKey
					.split(".")
					.reduce(
						(accum, currentValue) =>
							accum ? (accum as any)[currentValue] : undefined,
						allData
					) || "";

			return (
				<div className="flex flex-col">
					<p
						className={`${textSize} ${
							isDisabled && field.disabledClasses
						}`}
					>
						{value as string | number | boolean | T}
					</p>
					<p
						className={`${textSize} ${
							isDisabled && field.disabledClasses
						} ${field.secondValueTextClasses}`}
					>
						{`${field.secondValueLabel}: ${valueTwo}`}
					</p>
				</div>
			);
		} else if (field.textAfterValue && value !== "—") {
			return (
				<p
					className={`${textSize} ${
						isDisabled && field.disabledClasses
					}`}
				>
					{`${value} ${field.textAfterValue}`}
				</p>
			);
		} else {
			return (
				<p
					className={`${textSize} ${
						isDisabled && field.disabledClasses
					}`}
				>
					{value as string | number | boolean | T}
				</p>
			);
		}
	};

	return field && !field.isEditOnly ? (
		<div
			key={field.id}
			className={`mb-1 ${containerWidth} ${field.detailGridClasses}`}
		>
			<div className={`flex w-full  ${mobileBreakpoint}`}>
				{field.labelIcon === "true" && field.label && iconStyle ? (
					<div className={`pr-2 size-12`}>
						{getFontAwesomeIcon(
							field.label,
							iconStyle as "solid" | "regular" | "light"
						)}
					</div>
				) : (
					<div
						className={`${baseLabelClasses} ${labelClasses} ${
							isDisabled
								? field.disabledClasses
								: field.labelTextClasses
						} ${field.hideLabel ? "hidden" : ""}`}
					>
						<p className={`${textSize}`}>{field.label}</p>
					</div>
				)}
				<div
					className={`${baseDetailClasses} ${field.valueTextClasses}`}
				>
					{field.hasValueLeftIcon &&
						iconStyle &&
						dataItem !== "—" && (
							<div className={`px-1 ${field.detailIconClasses}`}>
								{getFontAwesomeIcon(
									field.hasValueLeftIcon,
									iconStyle
								)}
							</div>
						)}
					{renderValue(field, dataItem)}
					{field.hasValueRightIcon && iconStyle && (
						<div className="pl-6">
							{getFontAwesomeIcon(
								field.hasValueRightIcon,
								iconStyle
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	) : null;
};

export default OldBaseDetailRow;
